import { siteIDToAppID } from './AppID';
import { SiteID } from './SiteID';
import type { Brand } from './Brand';
import type { AppID } from './AppID';

/**
 * @deprecated Use {@link SiteID}, {@link AppID}, and {@link Brand} instead.
 *
 * Represents a Deckers Site which is distinct from the {@link Brand}. The site refers
 * to the specific site.
 *
 * For instance, `UGG-COLLAB` is a site, but it would still have a brand of `UGG`.
 * The site would be used to say access configuration, but the brand would be used to access
 * external resources related to that brand.
 *
 * For instance, you could look up brand orders, products, or accounts.
 *
 * NOTE: The Site key should equal the string value, just without dashes.
 * Example:  UGGCOLLAB = 'UGG-COLLAB'.
 * This is to ensure that the site key is a valid TypeScript identifier for static optimizations.
 */
export enum Site {
  /**
   * Represents the Sanuk brand.
   *
   * @see https://www.sanuk.com/
   */
  Sanuk = 'SANUK',

  /**
   * Represents the Teva brand.
   *
   * @see https://www.teva.com/
   */
  Teva = 'TEVA',

  /**
   * Represents the UGG brand.
   *
   * @see https://www.ugg.com/
   */
  UGG = 'UGG',

  /**
   * Represents the UGG Collaboration site.
   *
   * @see https://www.ugg.com/
   */
  UGGCOLLAB = 'UGG-COLLAB',

  /**
   * Represents the Hoka brand.
   *
   * @see https://www.hoka.com/
   */
  Hoka = 'HOKA',

  /**
   * Represents the Koolaburra brand.
   *
   * @see https://www.koolaburra.com/
   */
  Koolaburra = 'KOOLABURRA',

  /**
   * Represents the Ahnu brand.
   *
   * @see https://www.ahnu.com/
   */
  Ahnu = 'AHNU',

  /**
   * Represents the Deckers X Lab brand.
   *
   * @see https://deckersxlab.com/
   *
   * **IMPORTANT:** We do NOT manage this site. This value is only included for
   * parity with AWS, and should not be present in any real use case.
   */
  DeckersXLab = 'DECKERSX',

  /**
   * Represents the Conductor site used for analytics and other internal tools.
   */
  Conductor = 'CONDUCTOR'
}

/**
 * Gets the legacy {@link Site} associated with a given {@link SiteID}.
 * @param siteID - The {@link SiteID} to get the legacy site for.
 * @returns The corresponding legacy {@link Site}.
 * @throws An error if the `SiteID` does not correspond to a known `Site`.
 */
export function siteIDToLegacySite(siteID: SiteID): Site {
  try {
    return siteIDToAppID(siteID).toUpperCase() as Site;
  } catch (error) {
    throw new Error(`No legacy Site value found for SiteID: ${siteID}`);
  }
}
